import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { logError } from '@/utils/errorHandlerUtil';

type Args = {
  filterModel: GridFilterModel | null;
  sortModel: GridSortModel | null;
  paginationModel: GridPaginationModel;
  updateFilterModel: (f: GridFilterModel | null) => void;
  updateSortModel: (s: GridSortModel | null) => void;
  updatePaginationModel: (p: GridPaginationModel) => void;
  urlKeys: {
    filter: string;
    sort: string;
    page: string;
    pageSize: string;
  };
  sessionKeys: {
    filter: string;
    sort: string;
    page: string;
    pageSize: string;
  };
};

function parseJsonValue<T>(value: string | null): T | null {
  if (value == null) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function getStoredPage(value: string | null): number | null {
  if (value == null) return null;

  const page = Number(value);
  return Number.isNaN(page) || page < 0 ? null : page;
}

function getStoredPageSize(value: string | null): number | null {
  if (value == null) return null;

  const pageSize = Number(value);
  return Number.isNaN(pageSize) || pageSize <= 0 ? null : pageSize;
}

export default function useDashboardGridStatePersistence({
  filterModel,
  sortModel,
  paginationModel,
  updateFilterModel,
  updateSortModel,
  updatePaginationModel,
  urlKeys,
  sessionKeys,
}: Args) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const suppressPostHydrationZeroResetRef = useRef(false);
  const hydrationAppliedAtRef = useRef<number | null>(null);

  useEffect(() => {
    const filterFromUrl = searchParams.get(urlKeys.filter);
    const filterFromSession = parseJsonValue<GridFilterModel>(
      sessionStorage.getItem(sessionKeys.filter)
    );

    if (filterFromUrl != null) {
      try {
        updateFilterModel(JSON.parse(filterFromUrl) as GridFilterModel);
      } catch (e) {
        updateFilterModel(null);
        logError(`Error parsing ${urlKeys.filter} from URL.`, e);
      }
    } else if (filterFromSession != null) {
      updateFilterModel(filterFromSession);
    }

    const sortFromUrl = searchParams.get(urlKeys.sort);
    const sortFromSession = parseJsonValue<GridSortModel>(sessionStorage.getItem(sessionKeys.sort));

    if (sortFromUrl != null) {
      try {
        updateSortModel(JSON.parse(sortFromUrl) as GridSortModel);
      } catch (e) {
        updateSortModel(null);
        logError(`Error parsing ${urlKeys.sort} from URL.`, e);
      }
    } else if (sortFromSession != null) {
      updateSortModel(sortFromSession);
    }

    const pageFromUrl = getStoredPage(searchParams.get(urlKeys.page));
    const pageFromSession = getStoredPage(sessionStorage.getItem(sessionKeys.page));
    const pageSizeFromUrl = getStoredPageSize(searchParams.get(urlKeys.pageSize));
    const pageSizeFromSession = getStoredPageSize(sessionStorage.getItem(sessionKeys.pageSize));

    const hydratedPage = pageFromUrl ?? pageFromSession;
    const hydratedPageSize = pageSizeFromUrl ?? pageSizeFromSession;

    if (hydratedPage != null || hydratedPageSize != null) {
      const nextHydratedPage = hydratedPage ?? paginationModel.page;

      // DataGrid can emit a synthetic page=0 callback immediately after mount.
      // Only suppress that brief post-hydration reset when we restored a non-zero page.
      suppressPostHydrationZeroResetRef.current = nextHydratedPage > 0;
      hydrationAppliedAtRef.current = Date.now();

      updatePaginationModel({
        ...paginationModel,
        page: nextHydratedPage,
        pageSize: hydratedPageSize ?? paginationModel.pageSize,
      });
    }

    setIsHydrated(true);

    // intentionally one-time hydration from URL/session storage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    if (filterModel != null) {
      sessionStorage.setItem(sessionKeys.filter, JSON.stringify(filterModel));
    }

    if (sortModel != null) {
      sessionStorage.setItem(sessionKeys.sort, JSON.stringify(sortModel));
    }

    sessionStorage.setItem(sessionKeys.page, paginationModel.page.toString());
    sessionStorage.setItem(sessionKeys.pageSize, paginationModel.pageSize.toString());
  }, [
    filterModel,
    sortModel,
    paginationModel.page,
    paginationModel.pageSize,
    isHydrated,
    sessionKeys.filter,
    sessionKeys.page,
    sessionKeys.pageSize,
    sessionKeys.sort,
  ]);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    // Always merge with the latest URL to avoid clobbering params written by
    // other grid instances/effects in the same render cycle.
    const latestSearchParams = new URLSearchParams(window.location.search);
    const nextSearchParams = new URLSearchParams(latestSearchParams);
    let hasChanges = false;

    const currentFilterInUrl = latestSearchParams.get(urlKeys.filter);
    const currentSortInUrl = latestSearchParams.get(urlKeys.sort);
    const currentPageInUrl = latestSearchParams.get(urlKeys.page);
    const hasValidPageSizeKey =
      urlKeys.pageSize != null &&
      urlKeys.pageSize.trim() !== '' &&
      urlKeys.pageSize !== 'undefined';
    const currentPageSizeInUrl = hasValidPageSizeKey
      ? latestSearchParams.get(urlKeys.pageSize)
      : null;

    const nextFilterInUrl =
      filterModel != null && (filterModel.items?.length ?? 0) > 0
        ? JSON.stringify(filterModel)
        : null;
    const nextSortInUrl =
      sortModel != null && sortModel.length > 0 ? JSON.stringify(sortModel) : null;
    const nextPageInUrl = paginationModel.page.toString();
    const nextPageSizeInUrl = paginationModel.pageSize.toString();

    if (nextFilterInUrl !== currentFilterInUrl) {
      if (nextFilterInUrl == null) {
        nextSearchParams.delete(urlKeys.filter);
      } else {
        nextSearchParams.set(urlKeys.filter, nextFilterInUrl);
      }
      hasChanges = true;
    }

    if (nextSortInUrl !== currentSortInUrl) {
      if (nextSortInUrl == null) {
        nextSearchParams.delete(urlKeys.sort);
      } else {
        nextSearchParams.set(urlKeys.sort, nextSortInUrl);
      }
      hasChanges = true;
    }

    if (nextPageInUrl !== currentPageInUrl) {
      nextSearchParams.set(urlKeys.page, nextPageInUrl);
      hasChanges = true;
    }

    if (hasValidPageSizeKey && nextPageSizeInUrl !== currentPageSizeInUrl) {
      nextSearchParams.set(urlKeys.pageSize, nextPageSizeInUrl);
      hasChanges = true;
    }

    if (hasChanges) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    filterModel,
    isHydrated,
    paginationModel.page,
    paginationModel.pageSize,
    setSearchParams,
    sortModel,
    urlKeys.filter,
    urlKeys.page,
    urlKeys.pageSize,
    urlKeys.sort,
  ]);

  const handleFilterModelChange = useCallback(
    (nextFilterModel: GridFilterModel) => {
      updateFilterModel(nextFilterModel);
    },
    [updateFilterModel]
  );

  const handleSortModelChange = useCallback(
    (nextSortModel: GridSortModel) => {
      updateSortModel(nextSortModel);
    },
    [updateSortModel]
  );

  const handlePaginationModelChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      const pageSizeChanged = nextPaginationModel.pageSize !== paginationModel.pageSize;
      const resolvedPage = pageSizeChanged ? 0 : nextPaginationModel.page;
      const hydrationAgeMs =
        hydrationAppliedAtRef.current != null ? Date.now() - hydrationAppliedAtRef.current : null;
      const shouldSuppressPostHydrationZeroReset =
        suppressPostHydrationZeroResetRef.current &&
        resolvedPage === 0 &&
        paginationModel.page > 0 &&
        !pageSizeChanged &&
        hydrationAgeMs != null &&
        hydrationAgeMs < 3000;

      if (shouldSuppressPostHydrationZeroReset) {
        return;
      }

      if (
        resolvedPage !== 0 ||
        pageSizeChanged ||
        hydrationAgeMs == null ||
        hydrationAgeMs >= 3000
      ) {
        suppressPostHydrationZeroResetRef.current = false;
      }

      updatePaginationModel({
        ...nextPaginationModel,
        page: resolvedPage,
      });
    },
    [paginationModel.page, paginationModel.pageSize, updatePaginationModel]
  );

  return {
    isHydrated,
    handleFilterModelChange,
    handleSortModelChange,
    handlePaginationModelChange,
  };
}
